import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Step } from "./expense-form/constants";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { Plus, QrCode, PencilSimple, File } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import {
  useCreateTransactionAttachment,
  useAddExpenseTransaction,
} from "@/services/mutations/transactions";
import { useGetCurrencies } from "@/services/queries/currency";
import { useQueryClient } from "@tanstack/react-query";
import { formatDate, parseLocalDate, toDateOnlyString } from "@/lib/utils";
import useVessel from "@/store/use-vessel";
import { Mixpanel } from "@/lib/mixpanel";
import { DocumentUploadModal } from "@/components/document-upload/document-upload-modal";
import { Button } from "@/components/ui/button";
import { useCreateVendor } from "@/services/mutations/vendor";
import { useGetVendors } from "@/services/queries/vendor";
import { useNavigate } from "react-router-dom";
import { useGetVessels } from "@/services/queries/vessel";
import ExpenseForm, { ExpenseFormValues } from "./expense-form";
import TransferForm from "./transfer-form";
import { RiFileTransferLine } from "@remixicon/react";
import { Account } from "@/lib/types";
import { useGetAccounts } from "@/services/queries/accounts";
import { useConversionRate } from "@/hooks/use-conversion-rate";
import { z } from "zod";
import { startOfToday } from "date-fns";

const AddExpenseButton = ({ defaultDate }: { defaultDate?: Date }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<number>(Step.INITAL);
  const [open, setOpen] = useState(false);
  const [openTransferForm, setOpenTransferForm] = useState(false);
  const [showOptionsDrawer, setShowOptionsDrawer] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const { selectedVessel, setSelectedVessel } = useVessel();
  const [scannedInvoice, setScannedInvoice] = useState<File | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: createTransactionAttachment } =
    useCreateTransactionAttachment();
  const [selectedAccount, setSelectedAccount] = useState<Account | undefined>(
    undefined
  );

  // New entries default to the day picked in the header, else today. Read on
  // open, not memoized: the PWA can stay open past midnight.
  const pickedDay = () => defaultDate ?? startOfToday();

  const defaultValues = useMemo(
    () => ({
      amount: undefined,
      budgetId: "",
      currencyId: "",
      accountId: "",
      transactionDate: defaultDate ?? startOfToday(),
      files: [],
      description: "",
      vesselId: selectedVessel?.id ?? "",
      reference: "",
      vendor: null,
      vendorPaymentMethods: [],
    }),
    [selectedVessel, defaultDate]
  );

  const expenseFormSchema = z.object({
    amount: z.number().min(1),
    currencyId: z.string().min(1),
    reference: z.string().optional(),
    budgetId: z.string().min(1),
    budgetCategoryId: z.string().optional(),
    budgetSubcategoryId: z.string().optional(),

    accountId: z.string().min(1),
    transactionDate: z.date(),
    files: z.array(z.any()).optional(),
    description: z.string().optional(),
    vesselId: z.string().min(1),
    exchangeRate: z
      .number()
      .or(z.undefined())
      .refine((val) => {
        if (selectedAccount?.currencyId === form.getValues("currencyId")) {
          return true;
        }

        return val !== undefined;
      }),
    vendor: z
      .string()
      .or(z.object({ id: z.string().min(1) }))
      .or(z.null())
      .refine((value) => {
        if (value && typeof value === "object" && "id" in value) {
          return value.id.length > 0;
        } else if (value && typeof value === "string") {
          return value.length > 0;
        }
        return false;
      }), // Vendor can be a string (name), object (with id), or null
    vendorPaymentMethods: z.array(
      z.object({
        iban: z.string().optional(),
        bankName: z.string().optional(),
        swiftCode: z.string().optional(),
        accountHolderName: z.string().optional(),
        currencyId: z.string().optional(),
        bankAddress: z.string().optional(),
        bankAccountNumber: z.string().optional(),
        paymentTerms: z.string().optional(),
        isDefault: z.boolean().optional(),
      })
    ),
  });

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues,
  });

  const { data: currenciesData } = useGetCurrencies();
  const { data: vendorsData } = useGetVendors();
  const { data: vesselsData } = useGetVessels();
  const { data: accountsData } = useGetAccounts();
  // Auto-resolve the exchange rate at the transaction date — backdated
  // entries should use the historical rate, not today's. Also clears the
  // rate when currencies match, so submit never sends a stale value.
  const watchedCurrencyId = form.watch("currencyId");
  const watchedTransactionDate = form.watch("transactionDate");
  const transactionDateStr = watchedTransactionDate
    ? toDateOnlyString(watchedTransactionDate)
    : undefined;
  const { rate: exchangeRate, setRate: setExchangeRate } = useConversionRate({
    fromCurrencyId: watchedCurrencyId,
    toCurrencyId: selectedAccount?.currencyId,
    date: transactionDateStr,
  });

  useEffect(() => {
    form.setValue("exchangeRate", exchangeRate);
  }, [exchangeRate, form]);

  const accounts = useMemo(() => {
    return accountsData?.value ?? [];
  }, [accountsData?.value]);

  const currencies = useMemo(() => {
    return currenciesData?.value ?? [];
  }, [currenciesData?.value]);

  const vendors = useMemo(() => {
    return vendorsData?.value ?? [];
  }, [vendorsData?.value]);

  const vessels = useMemo(() => {
    return vesselsData?.value ?? [];
  }, [vesselsData?.value]);

  const handleScanInvoice = () => {
    setShowOptionsDrawer(false);

    // Create file input element for file selection
    const fileInput = document.createElement("input");
    fileInput.setAttribute("data-testid", "scan-invoice-file-input");
    fileInput.type = "file";
    fileInput.accept = "image/*,application/pdf";
    fileInput.capture = "environment"; // Prefer rear camera on mobile devices
    fileInput.multiple = false;
    fileInput.style.position = "fixed";
    fileInput.style.top = "-9999px";
    fileInput.style.left = "-9999px";

    // Append to DOM
    document.body.appendChild(fileInput);

    fileInput.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // Process the selected file
        processInvoiceImage(file);
      }

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Delay for closing the drawer and opening the file document upload modal
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fileInput.click();
      });
    });
  };

  const processInvoiceImage = (file: File) => {
    setShowDocumentUpload(true);
    setScannedInvoice(file);
  };

  const handleManualEntry = () => {
    setShowOptionsDrawer(false);
    // The form's date is only seeded on mount and on close, and the header
    // day can change while it's closed without remounting this button.
    form.setValue("transactionDate", pickedDay());
    setOpen(true);
  };

  // Hard guard against double-submit: a synchronous ref blocks duplicate
  // requests even when rapid taps fire before React re-renders (state-based
  // flags lag a render behind). Held true across the createVendor → addTransaction
  // chain; cleared when that chain settles (success or error).
  const isSubmittingRef = useRef(false);

  const { mutate: addTransaction, isPending: isAddingTransaction } =
    useAddExpenseTransaction({
      onSuccess: async ({ data }) => {
        if (form.getValues().files && form.getValues().files.length > 0) {
          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // transaction was already created, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled(
            form.getValues().files.map((file) =>
              createTransactionAttachment({
                transactionId: data.value.id,
                file: file as File,
              })
            )
          );
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["transactions-by-date-range"],
        });

        const vessel = vessels.find(
          (vessel) => vessel.id === form.watch("vesselId")
        );

        if (vessel) {
          setSelectedVessel(vessel);
          navigate(
            `/?date=${formatDate(form.watch("transactionDate") || new Date(), "yyyy-MM-dd")}`
          );
        }

        setOpen(false);
        form.reset();
        toast({
          title: t("success_message.add_expense_title"),
          description: t("success_message.add_expense_description"),
        });

        Mixpanel.track("Add Expense Successfully");
        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Add Expense Failed");
        isSubmittingRef.current = false;
      },
    });

  const { mutate: createVendor, isPending: isCreatingVendor } = useCreateVendor(
    {
      onSuccess: ({ value }) => {
        Mixpanel.track(
          "Vendor Addition Success When Adding Expense Transaction"
        );
        // Keep isSubmittingRef true — addTransaction's own handlers clear it.
        handleAddTransaction(form.getValues(), value.id);
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track(
          "Vendor Addition Failed When Adding Expense Transaction"
        );
        isSubmittingRef.current = false;
      },
    }
  );

  const onSubmit = (values: ExpenseFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (typeof values.vendor === "object" && values.vendor !== null) {
      handleAddTransaction(values, values.vendor.id);
    } else if (typeof values.vendor === "string") {
      const existingVendor = vendors.find(
        (vendor) =>
          vendor.name.toLowerCase() === (values.vendor as string).toLowerCase()
      );

      if (existingVendor) {
        handleAddTransaction(values, existingVendor.id);
      } else {
        createVendor({
          name: values.vendor,
          paymentMethods: (values.vendorPaymentMethods ?? []).map((item) => {
            return {
              ...item,
              name: values.vendor as string,
            };
          }),
        });
      }
    }
  };

  const handleAddTransaction = (
    values: ExpenseFormValues,
    vendorId: string
  ) => {
    addTransaction({
      ...values,
      accountId: values.accountId ?? "",
      amount: values.amount ?? 0,
      transactionDate: values.transactionDate
        ? toDateOnlyString(values.transactionDate)
        : "",
      reference: values.reference ?? "",
      budgetSubcategoryId: values.budgetSubcategoryId,
      vesselId: values.vesselId,
      vendorId: vendorId ?? "",
      accountConversionRate: values.exchangeRate,
    });
  };

  useEffect(() => {
    if (!showDocumentUpload) {
      setScannedInvoice(null);
    }
  }, [showDocumentUpload]);

  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.accountId) {
        const account = accounts.find(
          (account) => account.id === value.accountId
        );

        if (account) {
          setSelectedAccount(account);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, accounts]);

  return (
    <>
      <Drawer
        preventScrollRestoration={true}
        open={showOptionsDrawer}
        onOpenChange={setShowOptionsDrawer}
      >
        <DrawerTrigger asChild>
          <Button
            data-testid="add-transaction-button"
            type="button"
            className="h-8 gap-1.5 px-2 py-0"
          >
            <Plus className="size-4" />
            <span className="text-sm font-medium">
              {t("button.add_transaction")}
            </span>
          </Button>
        </DrawerTrigger>
        <DrawerContent
          data-testid="add-transaction-options-drawer"
          className="h-auto"
        >
          <h2 className="mx-auto mb-6 max-w-[200px] text-center text-lg text-white">
            {t("title.add_expense").toLocaleUpperCase(i18n.language)}
          </h2>
          <div className="flex flex-col gap-3">
            <button
              data-testid="scan-invoice-button"
              onClick={handleScanInvoice}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <QrCode size={24} />
              <span>{t("button.scan_receipt")}</span>
            </button>

            <button
              data-testid="upload-document-button"
              onClick={() => {
                setShowOptionsDrawer(false);
                setShowDocumentUpload(true);
              }}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <File size={24} />
              <span>{t("button.upload_file")}</span>
            </button>

            <button
              data-testid="manual-expense-button"
              onClick={handleManualEntry}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <PencilSimple size={24} />
              <span>{t("button.enter_manually")}</span>
            </button>

            <button
              data-testid="add-transfer-button"
              onClick={() => {
                setShowOptionsDrawer(false);
                setOpenTransferForm(true);
              }}
              className="flex w-full items-center justify-start gap-3 rounded-lg border border-white/20 px-4 py-4 text-base text-white"
            >
              <RiFileTransferLine size={24} />
              <span>{t("button.record_transfer")}</span>
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {showDocumentUpload && (
        <DocumentUploadModal
          open={showDocumentUpload}
          onOpenChange={setShowDocumentUpload}
          onViewDocument={({ files, documentResult }) => {
            setStep(Step.INITAL);
            setShowDocumentUpload(false);

            const vendor = vendors.find(
              (vendor) => vendor.name === documentResult.vendorName
            );

            form.reset({
              ...form.getValues(),
              files: files,
              vendor: vendor ? { id: vendor.id } : documentResult.vendorName,
              reference: documentResult.reference,
              // The API sends UTC midnight; new Date() would make it the
              // previous day west of UTC.
              transactionDate: documentResult.documentDate
                ? parseLocalDate(documentResult.documentDate)
                : pickedDay(),
              currencyId:
                currencies.find(
                  (currency) => currency.code === documentResult.currencyCode
                )?.id ?? "",
            });

            setOpen(true);
          }}
          initialFiles={scannedInvoice ? [scannedInvoice] : undefined}
        />
      )}

      <ExpenseForm
        open={open}
        setOpen={setOpen}
        form={form}
        step={step}
        setStep={setStep}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isSubmitting={isAddingTransaction || isCreatingVendor}
        initialVesselId={selectedVessel?.id}
        selectedAccount={selectedAccount}
        exchangeRate={exchangeRate}
        setExchangeRate={setExchangeRate}
      />

      <TransferForm
        title={t("title.add_transfer")}
        open={openTransferForm}
        setOpen={setOpenTransferForm}
        defaultDate={defaultDate}
      />
    </>
  );
};

export default AddExpenseButton;
